import { AxiosResponse } from 'axios';
import { APIConfig, throttle } from 'src/config';
import { IdValue } from 'src/interfaces/common/IdValue';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { AssigningModel, DiagnosisModel, GenericSparePart, RequestDetails,
  Document, StatusChangeRepairRequest, RescheduleRequest,
  RaiseRepairRequestModel,
  AddDiscount } from 'src/interfaces/repair-request/repair-request';
import { wait } from 'src/utils/wait';
import axiosClient from '../utils/axios-client';

export class RequestApi {
  private requestManagementUrl = APIConfig.requestManagementUrl;

  // calls POST api to add Technician in the database
  async raiseRepairRequest(requestDetails: RaiseRepairRequestModel):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}user/requests`, requestDetails).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // get request details by request Id
  async getRequestById(requestId: string):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.get(`${this.requestManagementUrl}requests/${requestId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // calls POST api to assignChannelPartner in the database
  async assignChannelPartner(assignChannelPartner: AssigningModel):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}requests/assign/channelPartner`, assignChannelPartner).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  // calls POST api to assignTechnician in the database
  async assignTechnician(assignTechnician: AssigningModel):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}requests/assign/technician`, assignTechnician).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  // calls PUT api to update diagnosis elements (symptoms, defects and repairAction) in the database
  async updateDiagnosis(diagnosis: DiagnosisModel, requestId: string):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}requests/${requestId}/diagnosis`, diagnosis).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  // calls PUT api add the sparePart in th repair request in the database
  async addSparePart(requestId: string, sparePart: GenericSparePart):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}requests/${requestId}/sparePart`, sparePart).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  // calls DELETE api to delete the sparePart from the repair request in the database
  async deleteSparePart(requestId: string, sparePartObjectId: string):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.delete(`${this.requestManagementUrl}requests/${requestId}/sparePart/${sparePartObjectId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  // calls POST api to add the comment in repair request in the database
  async addComment(comment: IdValue):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}comments`, comment).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  // calls POST api to add the comment in repair request in the database
  async getComments(comment: IdValue):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}comments`, comment).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  async getCommentsByPaginationAndFilter(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}comments/getByPaginationAndFilter`, options).then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async addDocument(requestId: string, documentData: Document):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}requests/${requestId}/document`, documentData).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  async deleteDocument(requestId: string, documentId: string):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.delete(`${this.requestManagementUrl}requests/${requestId}/document/${documentId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  async getRequestsByPaginationAndFilters(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient
        .post(`${this.requestManagementUrl}requests/getByPaginationAndFilter`, options)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async generateReport(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient
        .post(`${this.requestManagementUrl}requests/download/report`, options)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getRequestsFilterData():Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get(`${this.requestManagementUrl}requests/filters/data`);

    return response;
  }

  async updateStatusOfRequest(requestId: string, updateStatus: StatusChangeRepairRequest):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}requests/${requestId}/status`, updateStatus).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }

  async rescheduleRequest(options: RescheduleRequest):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient
        .patch(`${this.requestManagementUrl}requests/reschedule`, options)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async revisitRequest(options: RescheduleRequest):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient
        .patch(`${this.requestManagementUrl}requests/revisit`, options)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getChannelPartnerWiserequests(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}requests/getChannelPartnerWiseRequests`, options).then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getRequestsCountByFilters(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post(`${this.requestManagementUrl}requests/totalCount`, options).then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // calls PUT api to add the discount in the repair request in the database
  async addDiscount(requestId: string, discount: AddDiscount):
  Promise<{ responseData: RequestDetails, statusCode: number }> {
    try {
      const res = await axiosClient.put(`${this.requestManagementUrl}requests/${requestId}/discount`, discount).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.data,
        statusCode: error.status
      });
    }
  }
}

export const requestApi = new RequestApi();
